/**
 * UMULOO Theme JavaScript
 * Custom Shopify Theme
 */

(function() {
  'use strict';

  // ========================================
  // UTILITIES
  // ========================================
  
  const debounce = (fn, delay = 300) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const formatMoney = (cents, format = window.themeSettings?.moneyFormat || '${{amount}}') => {
    const amount = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.floor(cents / 100));
  };

  // Sanitize HTML to prevent XSS
  const sanitizeHTML = (str) => {
    const temp = document.createElement('div');
    temp.textContent = str;
    return temp.innerHTML;
  };

  const todayYmd = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getItemShipDate = (item) => {
    const props = item.properties || {};
    if (props._estimated_ship_date) return props._estimated_ship_date;
    if (window.preorderShipDates && window.preorderShipDates[item.product_id]) {
      return window.preorderShipDates[item.product_id];
    }
    return null;
  };

  const isPreorderActive = (isoDate) => {
    if (!isoDate) return false;
    return todayYmd() < isoDate;
  };

  const formatShipDate = (isoDate) => {
    const parts = String(isoDate).split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return isoDate;
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const preorderNoteHtml = (isoDate) => {
    if (!isPreorderActive(isoDate)) return '';
    return `
      <p class="preorder-note preorder-note--compact">
        <svg class="preorder-note__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="1" y="3" width="15" height="13" rx="1"/>
          <path d="M16 8h4l3 3v5h-7V8z"/>
          <circle cx="5.5" cy="18.5" r="2.5"/>
          <circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
        <span class="preorder-note__text">Preorder — estimated ship date: <strong>${sanitizeHTML(formatShipDate(isoDate))}</strong></span>
      </p>
    `;
  };

  // Max quantity per product - get from theme settings (defaults to 3)
  const MAX_QTY_PER_PRODUCT = (window.themeSettings && window.themeSettings.maxQuantityPerProduct) 
    ? parseInt(window.themeSettings.maxQuantityPerProduct) 
    : 3;

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  });

  // Focus Trap Utility for Modals/Drawers
  function trapFocus(container) {
    const focusableElements = container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    function handleTab(e) {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
    
    container.addEventListener('keydown', handleTab);
    firstElement.focus();
    
    return () => container.removeEventListener('keydown', handleTab);
  }

  // Toast Notification System - Disabled
  function showNotification(message, type = 'success', duration = 3000) {
    // Notifications disabled - function does nothing
    return;
  }
  
  function closeNotification(notification) {
    // Notifications disabled
    return;
  }

  // ========================================
  // ANIMATIONS
  // ========================================
  
  class ScrollAnimations {
    constructor() {
      this.animatedElements = document.querySelectorAll('[data-animate]');
      if (this.animatedElements.length === 0) return;
      
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );

      this.animatedElements.forEach((el) => this.observer.observe(el));
    }
  }

  // ========================================
  // CART
  // ========================================
  
  class Cart {
    constructor() {
      this.drawer = document.getElementById('cart-drawer');
      this.drawerContent = document.getElementById('cart-drawer-content');
      this.drawerFooter = document.getElementById('cart-drawer-footer');
      this.drawerTotal = document.getElementById('cart-drawer-total');
      this._quantityUpdateCounter = 0;
      this._quantityUpdateRequestIdByKey = {};
      this._quantityUpdateInFlight = new Set(); // prevent double-submit for same line

      this.bindEvents();
    }

    bindEvents() {
      // Toggle cart drawer
      document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggle());
      });

      // Close cart drawer
      document.querySelectorAll('[data-cart-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      // Delegated click for cart actions (qty +/- , remove) so it works even when content is updated in place
      if (this.drawerContent) {
        this.drawerContent.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action]');
          if (btn && this.drawerContent.contains(btn)) {
            this.handleItemAction(e, btn);
          }
        });
      }

      // Intercept add to cart forms (exclude product-form as it has its own handler)
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.tagName === 'FORM' && 
            form.id !== 'product-form' && // Skip product-form - handled by product page
            (form.action?.includes('/cart/add') || 
             form.action?.includes('cart_add_url') ||
             form.classList.contains('product-card__quick-add-form') ||
             form.hasAttribute('data-add-to-cart'))) {
          this.handleAddToCart(e);
        }
      });
      
      // Prevent form clicks from bubbling to parent link, but only when clicking the button
      document.addEventListener('click', (e) => {
        const quickAddBtn = e.target.closest('.product-card__quick-add, .product-card__quick-add-form button');
        if (quickAddBtn) {
          e.stopPropagation();
        }
      }, true); // Use capture phase to stop propagation early

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.drawer?.getAttribute('aria-hidden') === 'false';
    }

    lockScroll() {
      if (this._scrollLocked) return;
      this._scrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('cart-open');
      document.body.classList.add('cart-open');
      document.body.style.top = `-${this._scrollY}px`;
      this._scrollLocked = true;
    }

    unlockScroll() {
      if (!this._scrollLocked) return;
      document.documentElement.classList.remove('cart-open');
      document.body.classList.remove('cart-open');
      document.body.style.top = '';
      document.body.style.overflow = '';
      const y = this._scrollY || 0;
      this._scrollLocked = false;
      // Restore after unlocking position:fixed so iOS doesn't jump
      window.scrollTo(0, y);
    }

    async toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        await this.open();
      }
    }

    async open() {
      if (!this.drawer || this.isOpen()) return;

      this.lockScroll();

      // Dismiss cart notification if visible
      const cartNotification = document.getElementById('cart-notification');
      if (cartNotification) {
        cartNotification.setAttribute('aria-hidden', 'true');
      }

      // Paint the closed (off-screen) state first, then open on the next frames.
      // iOS Safari often skips the CSS transition if aria-hidden flips in the same tick.
      void this.drawer.offsetWidth;
      await new Promise((resolve) => requestAnimationFrame(resolve));
      this.drawer.setAttribute('aria-hidden', 'false');

      // Let the slide paint at least one frame before replacing drawer content
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await this.fetchCart();

      // Trap focus inside cart drawer
      this.removeFocusTrap = trapFocus(this.drawer.querySelector('.cart-drawer__panel, .cart-drawer__content') || this.drawer);
    }

    close() {
      if (!this.drawer) return;

      this.drawer.setAttribute('aria-hidden', 'true');
      this.unlockScroll();

      // Remove focus trap
      if (this.removeFocusTrap) {
        this.removeFocusTrap();
        this.removeFocusTrap = null;
      }
    }

    async fetchCart() {
      if (!this.drawerContent) return;
      
      // Show loading
      this.drawerContent.innerHTML = '<div class="cart-drawer__loading"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="16"/></svg></div>';
      
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        // Enforce max quantity limits
        const itemsToUpdate = cart.items.filter(item => item.quantity > MAX_QTY_PER_PRODUCT);
        if (itemsToUpdate.length > 0) {
          for (const item of itemsToUpdate) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: item.key, quantity: MAX_QTY_PER_PRODUCT })
            });
          }
          // Re-fetch after updates
          const updatedResponse = await fetch('/cart.js');
          const updatedCart = await updatedResponse.json();
          this.render(updatedCart);
        } else {
          this.render(cart);
        }
        
        // Also update product page cart qty if on a product page
        this.updateProductPageCartQty();
      } catch (error) {
        console.error('Error fetching cart:', error);
        this.drawerContent.innerHTML = '<div class="cart-drawer__empty"><p>Error loading cart. Please try again.</p></div>';
      }
    }

    render(cart) {
      if (!this.drawerContent) return;

      if (cart.item_count === 0) {
        this.drawerContent.innerHTML = `
          <div class="cart-drawer__empty">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <h3 class="cart-drawer__empty-title">Your cart is empty</h3>
            <p class="cart-drawer__empty-text">Add something to make it happy!</p>
            <a href="/collections/all" class="btn btn--primary">Start Shopping</a>
          </div>
        `;
        if (this.drawerFooter) this.drawerFooter.style.display = 'none';
        return;
      }

      // Show footer
      if (this.drawerFooter) this.drawerFooter.style.display = 'block';
      if (this.drawerTotal) this.drawerTotal.textContent = formatMoney(cart.total_price);

      // Group items by product_id
      const grouped = {};
      cart.items.forEach((item) => {
        if (!grouped[item.product_id]) {
          grouped[item.product_id] = {
            title: item.product_title,
            url: item.url.split('?')[0],
            image: item.image,
            shipDate: getItemShipDate(item),
            variants: []
          };
        } else if (!grouped[item.product_id].shipDate) {
          grouped[item.product_id].shipDate = getItemShipDate(item);
        }
        grouped[item.product_id].variants.push(item);
      });
      
      // Sort variants within each group by variant_id (reflects creation order)
      Object.values(grouped).forEach((product) => {
        product.variants.sort((a, b) => a.variant_id - b.variant_id);
      });

      // Render grouped items
      this.drawerContent.innerHTML = Object.values(grouped).map((product) => {
        const imageUrl = product.image ? product.image.replace(/(\.[^.]+)$/, '_200x$1') : '';
        const safeTitle = sanitizeHTML(product.title);
        const shipNote = preorderNoteHtml(product.shipDate);
        
        // Render each variant row
        const variantsHtml = product.variants.map((item) => {
          // Check limits - Dawn theme approach uses quantity_rule.max which includes inventory
          // Since we're using cart.js API, we need to check inventory manually if available
          let maxQty = MAX_QTY_PER_PRODUCT;
          let inventoryQty = null;
          let isInventoryLimited = false;
          let isInventoryTracked = false;
          
          // Check inventory if available (similar to how Dawn's quantity_rule.max handles it)
          if (window.variantInventory && window.variantInventory[item.variant_id]) {
            const invData = window.variantInventory[item.variant_id];
            inventoryQty = parseInt(invData.inventory_quantity);
            // Only use inventory limit if it's tracked and valid
            if (!isNaN(inventoryQty) && inventoryQty !== null && invData.inventory_management === 'shopify') {
              isInventoryTracked = true;
              // Use the lower of inventory and max order limit (Dawn's quantity_rule.max does this automatically)
              maxQty = Math.min(inventoryQty, MAX_QTY_PER_PRODUCT);
              isInventoryLimited = inventoryQty < MAX_QTY_PER_PRODUCT;
            }
          }
          
          const cantAddMore = item.quantity >= maxQty;
          
          // Create descriptive disabled reason
          let disabledReason = '';
          let stockNote = '';
          
          // Show low stock message when inventory is low (1-2) OR when limit is reached
          if (cantAddMore) {
            // Limit reached - show why
            if (isInventoryLimited) {
              // Inventory is the limiting factor
              stockNote = `Only ${maxQty} in stock`;
              disabledReason = stockNote;
            } else {
              // Max per order is the limiting factor (only show when max is actually set and reached)
              stockNote = `Max ${maxQty} per order`;
              disabledReason = stockNote;
            }
          } else if (isInventoryTracked && inventoryQty !== null && inventoryQty >= 1 && inventoryQty <= 2) {
            // Low stock warning - show even if limit not reached yet
            stockNote = `Only ${inventoryQty} in stock`;
          }
          
          const safeVariant = sanitizeHTML(item.variant_title || '');
          const showVariant = item.variant_title && item.variant_title !== 'Default Title';
          
          return `
            <div class="cart-drawer__variant-row" data-key="${sanitizeHTML(item.key)}" data-variant-id="${item.variant_id}">
              <div class="cart-drawer__variant-left">
                ${showVariant ? `<span class="cart-drawer__variant-name">${safeVariant}</span>` : ''}
                <div class="cart-drawer__variant-prices">
                  <span class="cart-drawer__variant-subtotal">${formatMoney(item.final_line_price)}</span>
                  ${item.quantity > 1 ? `<span class="cart-drawer__variant-unit">${formatMoney(item.final_price)} × ${item.quantity}</span>` : ''}
                </div>
              </div>
              <div class="cart-drawer__variant-right">
                <div class="cart-drawer__qty-controls">
                  <button type="button" class="cart-drawer__qty-btn" data-action="decrease" data-key="${sanitizeHTML(item.key)}" ${item.quantity <= 1 ? 'data-remove="true"' : ''}>
                    ${item.quantity <= 1 ? '×' : '−'}
                  </button>
                  <span class="cart-drawer__qty-value">${item.quantity}</span>
                  <button type="button" class="cart-drawer__qty-btn" data-action="increase" data-key="${sanitizeHTML(item.key)}" ${cantAddMore ? `disabled title="${disabledReason}"` : ''}>+</button>
                </div>
                ${stockNote ? `<span class="cart-drawer__qty-limit-note">${stockNote}</span>` : ''}
              </div>
            </div>
          `;
        }).join('');

        // Collect all variant keys for group removal
        const allKeys = product.variants.map(v => v.key).join(',');
        
        return `
          <div class="cart-drawer__product-group">
            <div class="cart-drawer__product-header">
              ${imageUrl ? `
              <div class="cart-drawer__product-image">
                <a href="${sanitizeHTML(product.url)}">
                  <img src="${sanitizeHTML(imageUrl)}" alt="${safeTitle}" width="60" height="60" loading="lazy">
                </a>
              </div>
              ` : ''}
              <a href="${sanitizeHTML(product.url)}" class="cart-drawer__product-title">${safeTitle}</a>
              <button type="button" class="cart-drawer__group-remove" data-action="remove-group" data-keys="${sanitizeHTML(allKeys)}" aria-label="Remove all ${safeTitle}">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            ${shipNote}
            <div class="cart-drawer__variants-list">
              ${variantsHtml}
            </div>
          </div>
        `;
      }).join('');

      // Events are delegated in bindEvents(); no per-button binding needed.
      
      // Load dynamic checkout buttons (Shop Pay, etc.)
      this.loadDynamicCheckoutButtons();
    }
    
    async loadDynamicCheckoutButtons() {
      const checkoutContainer = document.getElementById('cart-drawer-dynamic-checkout');
      if (!checkoutContainer) return;
      
      // Clear existing content
      checkoutContainer.innerHTML = '';
      
      try {
        // Fetch cart data to check if cart has items
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        if (cart.item_count === 0) {
          checkoutContainer.innerHTML = '';
          return;
        }
        
        // Create form structure that Shopify payment buttons expect
        const form = document.createElement('form');
        form.action = '/cart';
        form.method = 'post';
        form.enctype = 'multipart/form-data';
        form.noValidate = true;
        form.className = 'cart-drawer__checkout-form';
        
        // Create payment button container - Shopify will auto-detect and initialize this
        const paymentButtonContainer = document.createElement('div');
        paymentButtonContainer.className = 'shopify-payment-button';
        paymentButtonContainer.setAttribute('data-shopify', 'payment-button');
        
        form.appendChild(paymentButtonContainer);
        checkoutContainer.appendChild(form);
        
        // Initialize Shopify payment buttons after DOM is ready
        // Shopify's payment button script should auto-detect the form, but we can also manually trigger
        setTimeout(() => {
          // Try multiple initialization methods
          if (window.Shopify && window.Shopify.dynamicPaymentButtons) {
            window.Shopify.dynamicPaymentButtons.init();
          }
          
          // Trigger Shopify's payment button initialization if available
          if (window.Shopify && window.Shopify.PaymentButton) {
            window.Shopify.PaymentButton.init();
          }
          
          // Dispatch custom event that some themes listen for
          const initEvent = new CustomEvent('shopify:payment-button:initialize');
          document.dispatchEvent(initEvent);
          
          // Also try to trigger via DOMContentLoaded-like event
          if (document.readyState === 'complete' || document.readyState === 'interactive') {
            const loadEvent = new Event('load');
            window.dispatchEvent(loadEvent);
          }
        }, 300);
        
      } catch (error) {
        console.error('Error loading dynamic checkout buttons:', error);
        checkoutContainer.innerHTML = '';
      }
    }

    async handleItemAction(e, btnOverride) {
      e.preventDefault();
      e.stopPropagation();
      const btn = btnOverride || e.currentTarget;
      if (!btn || !btn.dataset.action) return;
      const action = btn.dataset.action;
      const key = btn.dataset.key;
      const itemEl = btn.closest('.cart-drawer__variant-row') || btn.closest('.cart-drawer__item');
      const variantId = itemEl?.dataset.variantId;
      
      // Add loading state to button
      btn.classList.add('is-loading');
      btn.disabled = true;
      
      // Blur the button immediately to remove focus/highlight state on mobile
      btn.blur();
      
      try {
        if (action === 'remove') {
          // Start leave animation immediately; don't delay the cart API for it
          if (itemEl) {
            itemEl.classList.add('removing');
            itemEl.dataset.removingAt = String(Date.now());
          }
          await this.updateQuantity(key, 0, variantId, itemEl);
        } else if (action === 'remove-group') {
          // Animate out the product group while requests run
          const groupEl = btn.closest('.cart-drawer__product-group');
          if (groupEl) {
            groupEl.classList.add('removing');
            groupEl.dataset.removingAt = String(Date.now());
          }
          // Remove all variants in this product group
          const keys = btn.dataset.keys?.split(',') || [];
          for (const k of keys) {
            const escapedKey = k.trim().replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, '\\$&');
            const variantRow = this.drawerContent.querySelector(`[data-key="${escapedKey}"]`);
            if (variantRow) {
              variantRow.classList.add('removing');
              variantRow.dataset.removingAt = String(Date.now());
            }
            await this.updateQuantity(k.trim(), 0, null, variantRow);
          }
        } else if (action === 'decrease') {
          const currentQty = parseInt(itemEl.querySelector('.cart-drawer__qty-value').textContent);
          const willRemove = currentQty <= 1;
          if (willRemove && itemEl) {
            // Start leave animation immediately; don't delay the cart API for it
            itemEl.classList.add('removing');
            itemEl.dataset.removingAt = String(Date.now());
          }
          await this.updateQuantity(key, Math.max(0, currentQty - 1), variantId, willRemove ? itemEl : null);
        } else if (action === 'increase') {
          const currentQty = parseInt(itemEl.querySelector('.cart-drawer__qty-value').textContent);
          
          // Check limits - Dawn approach uses quantity_rule.max (includes inventory)
          let maxQty = MAX_QTY_PER_PRODUCT;
          
          // Check inventory if available (Dawn's quantity_rule.max handles this automatically)
          if (window.variantInventory && window.variantInventory[variantId]) {
            const invData = window.variantInventory[variantId];
            const inventoryQty = parseInt(invData.inventory_quantity);
            // Only use inventory limit if it's tracked and valid
            if (!isNaN(inventoryQty) && inventoryQty !== null && invData.inventory_management === 'shopify') {
              // Use the lower of inventory and max order limit
              maxQty = Math.min(inventoryQty, MAX_QTY_PER_PRODUCT);
            }
          }
          
          if (currentQty >= maxQty) {
            btn.classList.remove('is-loading');
            // Keep button disabled since we're at max
            btn.disabled = true;
            btn.setAttribute('disabled', 'disabled');
            return; // Already at max
          }
          
          await this.updateQuantity(key, currentQty + 1, variantId);
        }
      } finally {
        // Remove loading state from button (if still exists)
        if (btn.parentNode) {
          btn.classList.remove('is-loading');
          
          // Re-enable decrease button (it should always be clickable unless item was removed)
          if (action === 'decrease' || action === 'remove') {
            const itemRow = btn.closest('.cart-drawer__variant-row') || btn.closest('.cart-drawer__item');
            if (itemRow && !itemRow.classList.contains('removing')) {
              btn.disabled = false;
              btn.removeAttribute('disabled');
            }
          } else if (action === 'increase') {
            // For increase button, let updateCartItem handle the disabled state
            // based on whether max quantity is reached
          }
        }
      }
    }

    async updateQuantity(key, quantity, variantId, itemElToRemove = null) {
      const keyStr = String(key);
      if (this._quantityUpdateInFlight.has(keyStr)) return; // one update per line at a time
      this._quantityUpdateInFlight.add(keyStr);
      const requestId = ++this._quantityUpdateCounter;
      this._quantityUpdateRequestIdByKey[keyStr] = requestId;
      try {
        // Enforce max per order limit
        let finalQuantity = Math.min(Math.max(0, quantity), MAX_QTY_PER_PRODUCT);
        
        // Also enforce inventory limit if we have the data
        if (variantId && window.variantInventory && window.variantInventory[variantId]) {
          const invData = window.variantInventory[variantId];
          const inventoryQty = parseInt(invData.inventory_quantity) || 999;
          finalQuantity = Math.min(finalQuantity, inventoryQty);
        }
        
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: finalQuantity })
        });

        if (response.ok) {
          const cart = await response.json();
          // Ignore stale response for this line only (so remove-group and other lines still apply)
          if (this._quantityUpdateRequestIdByKey[keyStr] !== requestId) {
            this._quantityUpdateInFlight.delete(keyStr);
            return;
          }
          delete this._quantityUpdateRequestIdByKey[keyStr];

          // Update only what changed instead of full re-render
          if (finalQuantity === 0) {
            // Totals/count as soon as Shopify responds — don't wait on leave animation
            this.updateCartTotals(cart);
            this.updateCartCount();
            this.updateProductPageCartQty();

            // Item was removed - use provided element or find it
            let itemEl = itemElToRemove;
            if (!itemEl) {
              // Find the item element by key (escape special characters for CSS selector)
              const escapedKey = key.replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, '\\$&');
              itemEl = this.drawerContent.querySelector(`[data-key="${escapedKey}"]`);
            }
            
            if (itemEl) {
              const productGroup = itemEl.closest('.cart-drawer__product-group');
              
              // Ensure removal animation class is applied if not already
              if (!itemEl.classList.contains('removing')) {
                itemEl.classList.add('removing');
                itemEl.dataset.removingAt = String(Date.now());
                await new Promise(r => setTimeout(r, 250));
              } else {
                // Animation started with the request; wait out the rest of the 250ms leave
                const started = Number(itemEl.dataset.removingAt) || Date.now();
                const remaining = Math.max(0, 250 - (Date.now() - started));
                if (remaining > 0) await new Promise(r => setTimeout(r, remaining));
              }
              
              if (productGroup) {
                // Check if there are other variants in this group (excluding the one being removed)
                const allVariants = productGroup.querySelectorAll('.cart-drawer__variant-row');
                const remainingVariants = Array.from(allVariants).filter(v => 
                  !v.classList.contains('removing') && v !== itemEl
                );
                if (remainingVariants.length === 0) {
                  // This was the last variant, remove the entire group
                  if (!productGroup.classList.contains('removing')) {
                    productGroup.classList.add('removing');
                    await new Promise(r => setTimeout(r, 250));
                  }
                  productGroup.remove();
                } else {
                  // Just remove this variant row after animation
                  itemEl.remove();
                }
              } else {
                // Old format item, remove after animation
                itemEl.remove();
              }
            } else {
              // Could not find row in DOM; refresh whole drawer
              await this.fetchCart();
              return;
            }
            
            // If cart is now empty, show empty state
            if (cart.item_count === 0) {
              setTimeout(() => {
                this.drawerContent.innerHTML = `
                  <div class="cart-drawer__empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <path d="M16 10a4 4 0 0 1-8 0"/>
                    </svg>
                    <h3 class="cart-drawer__empty-title">Your cart is empty</h3>
                    <p class="cart-drawer__empty-text">Add something to make it happy!</p>
                    <a href="/collections/all" class="btn btn--primary">Start Shopping</a>
                  </div>
                `;
                if (this.drawerFooter) this.drawerFooter.style.display = 'none';
              }, 100);
            }
          } else {
            // Item quantity changed - match key loosely (API may return number or string)
            const updatedItem = cart.items.find(item => String(item.key) === keyStr);
            if (updatedItem) {
              const updated = this.updateCartItem(key, updatedItem);
              if (!updated) await this.fetchCart(); // element not found, refresh whole drawer
            } else {
              await this.fetchCart(); // key mismatch or item missing, refresh
            }
            this.updateCartTotals(cart);
            this.updateCartCount();
            this.updateProductPageCartQty();
          }
        }
      } catch (error) {
        console.error('Error updating quantity:', error);
        delete this._quantityUpdateRequestIdByKey[keyStr];
        await this.fetchCart();
      } finally {
        this._quantityUpdateInFlight.delete(keyStr);
      }
    }
    
    // Update a single cart item's display without re-rendering everything. Returns false if element not found.
    updateCartItem(key, item) {
      if (!item) return false;
      const keyStr = String(key);
      // Find the item element: try exact key then by matching data-key (Shopify keys are often numeric)
      let itemEl = this.drawerContent.querySelector(`[data-key="${keyStr}"]`);
      if (!itemEl) {
        const escapedKey = keyStr.replace(/[!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~]/g, '\\$&');
        itemEl = this.drawerContent.querySelector(`[data-key="${escapedKey}"]`);
      }
      if (!itemEl) return false;
      
      // Update quantity
      const qtyValue = itemEl.querySelector('.cart-drawer__qty-value');
      if (qtyValue) {
        qtyValue.textContent = item.quantity;
      }
      
      // Update prices
      const subtotalEl = itemEl.querySelector('.cart-drawer__variant-subtotal');
      if (subtotalEl) {
        subtotalEl.textContent = formatMoney(item.final_line_price);
      }
      
      const unitPriceEl = itemEl.querySelector('.cart-drawer__variant-unit');
      if (item.quantity > 1) {
        if (!unitPriceEl) {
          const pricesEl = itemEl.querySelector('.cart-drawer__variant-prices');
          if (pricesEl && subtotalEl) {
            const unitEl = document.createElement('span');
            unitEl.className = 'cart-drawer__variant-unit';
            subtotalEl.after(unitEl);
            unitEl.textContent = `${formatMoney(item.final_price)} × ${item.quantity}`;
          }
        } else {
          unitPriceEl.textContent = `${formatMoney(item.final_price)} × ${item.quantity}`;
        }
      } else if (unitPriceEl) {
        unitPriceEl.remove();
      }
      
      // Update decrease button
      const decreaseBtn = itemEl.querySelector('[data-action="decrease"]');
      if (decreaseBtn) {
        // Always ensure decrease button is enabled (it should always be clickable)
        decreaseBtn.disabled = false;
        decreaseBtn.removeAttribute('disabled');
        
        if (item.quantity <= 1) {
          decreaseBtn.setAttribute('data-remove', 'true');
          decreaseBtn.innerHTML = '×';
        } else {
          decreaseBtn.removeAttribute('data-remove');
          decreaseBtn.innerHTML = '−';
        }
      }
      
      // Update increase button and limit note
      const increaseBtn = itemEl.querySelector('[data-action="increase"]');
      const limitNote = itemEl.querySelector('.cart-drawer__qty-limit-note');
      
      let maxQty = MAX_QTY_PER_PRODUCT;
      let inventoryQty = null;
      let isInventoryLimited = false;
      let isInventoryTracked = false;
      
      // Get variant_id from item or from the element's data attribute
      const variantId = item.variant_id ? parseInt(item.variant_id) : parseInt(itemEl.dataset.variantId);
      
      if (variantId && window.variantInventory && window.variantInventory[variantId]) {
        const invData = window.variantInventory[variantId];
        inventoryQty = parseInt(invData.inventory_quantity);
        if (!isNaN(inventoryQty) && inventoryQty !== null && invData.inventory_management === 'shopify') {
          isInventoryTracked = true;
          maxQty = Math.min(inventoryQty, MAX_QTY_PER_PRODUCT);
          isInventoryLimited = inventoryQty < MAX_QTY_PER_PRODUCT;
        }
      }
      
      const cantAddMore = item.quantity >= maxQty;
      
      if (increaseBtn) {
        if (cantAddMore) {
          const disabledReason = isInventoryLimited ? `Only ${maxQty} in stock` : `Max ${maxQty} per order`;
          increaseBtn.disabled = true;
          increaseBtn.setAttribute('disabled', 'disabled');
          increaseBtn.setAttribute('title', disabledReason);
        } else {
          increaseBtn.disabled = false;
          increaseBtn.removeAttribute('disabled');
          increaseBtn.removeAttribute('title');
        }
        
        // Show low stock message when inventory is low (1-2) OR when limit is reached
        let stockNoteText = '';
        if (cantAddMore) {
          // Limit reached - show why
          if (isInventoryLimited) {
            // Inventory is the limiting factor
            stockNoteText = `Only ${maxQty} in stock`;
          } else {
            // Max per order is the limiting factor (only show when max is reached)
            stockNoteText = `Max ${maxQty} per order`;
          }
        } else if (isInventoryTracked && inventoryQty !== null && inventoryQty >= 1 && inventoryQty <= 2) {
          // Low stock warning - show even if limit not reached yet
          stockNoteText = `Only ${inventoryQty} in stock`;
        }
        
        if (stockNoteText) {
          if (!limitNote) {
            const note = document.createElement('span');
            note.className = 'cart-drawer__qty-limit-note';
            note.textContent = stockNoteText;
            const qtyControls = itemEl.querySelector('.cart-drawer__qty-controls');
            if (qtyControls) {
              qtyControls.after(note);
            }
          } else {
            limitNote.textContent = stockNoteText;
          }
        } else if (limitNote) {
          // Remove the note if limit is not reached
          limitNote.remove();
        }
      }
      return true;
    }
    
    // Update cart totals without re-rendering
    updateCartTotals(cart) {
      if (this.drawerTotal) {
        this.drawerTotal.textContent = formatMoney(cart.total_price);
      }
    }
    
    // Update the cart qty display on product page (if on a product page)
    async updateProductPageCartQty() {
      const cartQtyDisplay = document.getElementById('cart-qty-display');
      const variantIdInput = document.getElementById('variant-id');
      
      if (!cartQtyDisplay || !variantIdInput) return;
      
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const currentVariantId = parseInt(variantIdInput.value);
        
        const cartItem = cart.items.find(item => item.variant_id === currentVariantId);
        const qtyInCart = cartItem ? cartItem.quantity : 0;
        
        cartQtyDisplay.textContent = qtyInCart > 0 ? qtyInCart + ' in cart' : '';
      } catch (e) {
        console.error('Error updating product page cart qty:', e);
      }
    }

    async handleAddToCart(e) {
      e.preventDefault();
      
      const form = e.target;
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn?.innerHTML;
      
      if (submitBtn) {
        submitBtn.innerHTML = '<span style="display: inline-flex; align-items: center; gap: 0.5rem;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="animate-spin"><circle cx="12" cy="12" r="10" stroke-dasharray="32" stroke-dashoffset="16"/></svg> Adding...</span>';
        submitBtn.disabled = true;
      }

      try {
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const requestedQty = parseInt(formData.get('quantity')) || 1;
        
        // Check current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        const existingItem = cart.items.find(item => item.variant_id == variantId);
        const currentQty = existingItem ? parseInt(existingItem.quantity) : 0;
        
        // STRICT CHECK: Can't add if already at max
        if (currentQty >= MAX_QTY_PER_PRODUCT) {
          if (submitBtn) {
            submitBtn.innerHTML = `<span style="color: #e74c3c;">Max ${MAX_QTY_PER_PRODUCT} per order (you have ${currentQty})</span>`;
            setTimeout(() => {
              submitBtn.innerHTML = originalText;
              submitBtn.disabled = false;
            }, 2500);
          }
          return;
        }
        
        // Calculate how many more can be added
        const canAddMore = MAX_QTY_PER_PRODUCT - currentQty;
        
        // Can't add 0 or negative
        if (canAddMore <= 0) {
          if (submitBtn) {
            submitBtn.innerHTML = `<span style="color: #e74c3c;">Max ${MAX_QTY_PER_PRODUCT} per order</span>`;
            setTimeout(() => {
              submitBtn.innerHTML = originalText;
              submitBtn.disabled = false;
            }, 2500);
          }
          return;
        }
        
        // Limit quantity to what can be added
        const actualQty = Math.min(requestedQty, canAddMore);
        
        // Final check: ensure total won't exceed max
        if (currentQty + actualQty > MAX_QTY_PER_PRODUCT) {
          const allowedQty = MAX_QTY_PER_PRODUCT - currentQty;
          if (allowedQty <= 0) {
            if (submitBtn) {
              submitBtn.innerHTML = `<span style="color: #e74c3c;">Max ${MAX_QTY_PER_PRODUCT} per order</span>`;
              setTimeout(() => {
                submitBtn.innerHTML = originalText;
                submitBtn.disabled = false;
              }, 2500);
            }
            return;
          }
          formData.set('quantity', allowedQty.toString());
        } else {
          formData.set('quantity', actualQty.toString());
        }
        
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const addedProduct = await response.json();
          const productName = addedProduct.product_title || 'Item';

          // Keep preorder ship-date map in sync for cart drawer notes
          const shipDate =
            (addedProduct.properties && addedProduct.properties._estimated_ship_date) ||
            formData.get('properties[_estimated_ship_date]');
          if (shipDate && addedProduct.product_id) {
            window.preorderShipDates = window.preorderShipDates || {};
            window.preorderShipDates[addedProduct.product_id] = shipDate;
          }
          
          // Notification disabled
          
          // Reset quantity input to 1
          const qtyInput = form.querySelector('#qty-input, [name="quantity"][type="number"]');
          if (qtyInput) {
            qtyInput.value = 1;
          }
          
          // Update cart variant quantity map
          window.cartVariantQty = window.cartVariantQty || {};
          window.cartVariantQty[variantId] = currentQty + actualQty;
          
          // Update cart qty display next to max qty (live update)
          const cartQtyDisplay = document.getElementById('cart-qty-display');
          if (cartQtyDisplay) {
            const newQty = currentQty + actualQty;
            cartQtyDisplay.textContent = newQty > 0 ? newQty + ' in cart' : '';
          }
          
          // Update stock display if function exists
          if (typeof window.updateStockDisplay === 'function') {
            window.updateStockDisplay();
          }
          
          // Update cart count
          this.updateCartCount();
          
          // If cart drawer is open, refresh it to show new item
          if (this.isOpen()) {
            await this.fetchCart();
          }
          
          // Reset button
          if (submitBtn) {
            // Clear any existing timeout
            if (submitBtn.dataset.resetTimeout) {
              clearTimeout(parseInt(submitBtn.dataset.resetTimeout));
            }
            
            const addedMsg = actualQty < requestedQty 
              ? `Added ${actualQty} (max ${MAX_QTY_PER_PRODUCT})`
              : 'Added!';
            submitBtn.innerHTML = `<span style="display: inline-flex; align-items: center; gap: 0.5rem;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg> ${addedMsg}</span>`;
            
            // Store timeout ID on button so it can be cleared when variant changes
            const timeoutId = setTimeout(() => {
              submitBtn.innerHTML = originalText;
              submitBtn.disabled = false;
              delete submitBtn.dataset.resetTimeout;
            }, 2500);
            submitBtn.dataset.resetTimeout = timeoutId.toString();
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        if (submitBtn) {
          // Clear any existing timeout
          if (submitBtn.dataset.resetTimeout) {
            clearTimeout(parseInt(submitBtn.dataset.resetTimeout));
          }
          
          submitBtn.innerHTML = error.message.includes('not enough') 
            ? 'Out of Stock' 
            : 'Error - Try Again';
          
          const timeoutId = setTimeout(() => {
            submitBtn.innerHTML = originalText;
            submitBtn.disabled = false;
            delete submitBtn.dataset.resetTimeout;
          }, 2500);
          submitBtn.dataset.resetTimeout = timeoutId.toString();
        }
      }
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        let countEl = document.querySelector('.header__cart-count');
        const cartBtn = document.querySelector('.header__cart-btn');
        
        // Create count element if it doesn't exist
        if (!countEl && cartBtn && cart.item_count > 0) {
          countEl = document.createElement('span');
          countEl.className = 'header__cart-count';
          countEl.style.cssText = 'position:absolute;top:4px;right:4px;min-width:18px;height:18px;background:var(--color-primary);color:white;font-size:10px;font-weight:700;border-radius:50%;display:flex;align-items:center;justify-content:center;';
          cartBtn.appendChild(countEl);
        }
        
        // Update the count
        if (countEl) {
          countEl.textContent = cart.item_count;
          countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      } catch (error) {
        console.error('Error updating cart count:', error);
      }
    }
  }

  // ========================================
  // PRODUCT GALLERY
  // ========================================
  
  class ProductGallery {
    constructor() {
      this.gallery = document.querySelector('[data-product-gallery]');
      if (!this.gallery) return;

      this.mainImage = this.gallery.querySelector('[data-main-image] img');
      this.thumbnails = this.gallery.querySelectorAll('[data-thumbnail]');

      this.bindEvents();
    }

    bindEvents() {
      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => this.handleThumbnailClick(thumb));
      });
    }

    handleThumbnailClick(thumb) {
      const src = thumb.dataset.src;
      
      if (this.mainImage) {
        this.mainImage.style.opacity = '0';
        setTimeout(() => {
          this.mainImage.src = src;
          this.mainImage.style.opacity = '1';
        }, 200);
      }

      this.thumbnails.forEach((t) => t.classList.remove('active'));
      thumb.classList.add('active');
    }
  }

  // ========================================
  // PRODUCT VARIANTS
  // ========================================
  
  class ProductVariants {
    constructor() {
      this.form = document.querySelector('[data-product-form]') || document.getElementById('product-form');
      if (!this.form) return;

      this.productJson = document.querySelector('[data-product-json]');
      if (!this.productJson) return;

      try {
        this.product = JSON.parse(this.productJson.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
        return;
      }

      this.variantInput = this.form.querySelector('[data-variant-id]') || document.getElementById('variant-id');
      this.priceElement = document.querySelector('[data-price]') || document.getElementById('product-price');
      this.comparePriceElement = document.querySelector('[data-compare-price]') || document.getElementById('product-compare-price');
      this.addToCartBtn = document.querySelector('[data-add-to-cart-btn]') || document.getElementById('add-to-cart-btn');
      
      // Only bind if we have the necessary elements
      if (this.variantInput && this.product) {
        this.bindEvents();
      }
    }

    bindEvents() {
      // Watch for variant ID changes (triggered by inline script)
      if (this.variantInput) {
        let lastVariantId = this.variantInput.value;
        
        // Watch for input/change events
        this.variantInput.addEventListener('input', () => {
          if (this.variantInput.value !== lastVariantId) {
            lastVariantId = this.variantInput.value;
            setTimeout(() => this.checkVariantChange(), 50);
          }
        });
        
        this.variantInput.addEventListener('change', () => {
          if (this.variantInput.value !== lastVariantId) {
            lastVariantId = this.variantInput.value;
            this.checkVariantChange();
          }
        });
        
        // Watch for clicks on variant options (inline script updates variant-id after click)
        document.addEventListener('click', (e) => {
          const btn = e.target.closest('.product__variant-option');
          if (btn && btn.dataset.optionValue) {
            // Wait for inline script to update variant-id, then check
            setTimeout(() => {
              if (this.variantInput.value !== lastVariantId) {
                lastVariantId = this.variantInput.value;
                this.checkVariantChange();
              }
            }, 150);
          }
        });
      }
    }
    
    checkVariantChange() {
      if (!this.variantInput || !this.product) return;
      
      const variantId = parseInt(this.variantInput.value);
      if (!variantId) return;
      
      const variant = this.product.variants.find(v => v.id === variantId);
      if (variant) {
        this.updateVariant(variant);
      }
    }

    updateVariant(variant) {
      // Update hidden input
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      // Update price
      if (this.priceElement) {
        this.priceElement.textContent = formatMoney(variant.price);
      }

      // Update compare price
      if (this.comparePriceElement) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.comparePriceElement.textContent = formatMoney(variant.compare_at_price);
          this.comparePriceElement.style.display = '';
        } else {
          this.comparePriceElement.style.display = 'none';
        }
      }

      // Update inventory quantity data attribute (let updateStockDisplay handle the max)
      const qtyInput = document.getElementById('qty-input');
      if (qtyInput) {
        const variantInventory = variant.inventory_quantity !== null ? variant.inventory_quantity : 999;
        qtyInput.setAttribute('data-inventory-qty', variantInventory);
        // Don't set max here - let updateStockDisplay calculate it based on available (inventory - in cart)
        // Call updateStockDisplay if it exists
        if (typeof window.updateStockDisplay === 'function') {
          window.updateStockDisplay();
        }
      }

      // Reset button to default state (CSS handles visibility via classes)
      if (this.addToCartBtn) {
        this.addToCartBtn.classList.remove('is-loading', 'is-success');
        
        const btnText = this.addToCartBtn.querySelector('.product__add-btn-text');
        if (variant.available) {
          this.addToCartBtn.disabled = false;
          if (btnText) btnText.textContent = 'Add to Cart';
        } else {
          this.addToCartBtn.disabled = true;
          if (btnText) btnText.textContent = 'Sold Out';
        }
      }

      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update main image if variant has featured image
      if (variant.featured_image) {
        const mainImage = document.querySelector('[data-main-image] img');
        if (mainImage) {
          mainImage.style.opacity = '0';
          setTimeout(() => {
            mainImage.src = variant.featured_image.src;
            mainImage.style.opacity = '1';
          }, 200);
        }
      }
    }
  }

  // ========================================
  // QUANTITY SELECTOR
  // ========================================
  
  class QuantitySelector {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      document.querySelectorAll('[data-quantity-minus], #qty-minus').forEach((btn) => {
        btn.addEventListener('click', (e) => this.decrease(e));
      });

      document.querySelectorAll('[data-quantity-plus], #qty-plus').forEach((btn) => {
        btn.addEventListener('click', (e) => this.increase(e));
      });
      
      // Also enforce on direct input
      document.querySelectorAll('[data-quantity-input], #qty-input').forEach((input) => {
        input.addEventListener('change', (e) => this.validateInput(e));
      });
    }

    decrease(e) {
      const container = e.target.closest('.product__quantity-selector');
      const input = container?.querySelector('[data-quantity-input], #qty-input') || document.getElementById('qty-input');
      if (!input) return;
      
      const value = parseInt(input.value);
      if (value > 1) {
        input.value = value - 1;
      }
    }

    increase(e) {
      const container = e.target.closest('.product__quantity-selector');
      const input = container?.querySelector('[data-quantity-input], #qty-input') || document.getElementById('qty-input');
      if (!input) return;
      
      const value = parseInt(input.value);
      const max = parseInt(input.max) || 999;
      
      if (value < max) {
        input.value = value + 1;
      }
    }
    
    validateInput(e) {
      const input = e.target;
      let value = parseInt(input.value) || 1;
      const max = parseInt(input.max) || 999;
      
      if (value < 1) value = 1;
      if (value > max) value = max;
      
      input.value = value;
    }
  }

  // ========================================
  // QUICK VIEW
  // ========================================
  
  class QuickView {
    constructor() {
      this.modal = document.getElementById('quick-view-modal');
      this.modalContent = document.getElementById('quick-view-content');
      
      if (!this.modal) return;
      
      this.bindEvents();
    }

    bindEvents() {
      document.querySelectorAll('[data-quick-view]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.open(btn.dataset.quickView);
        });
      });

      document.querySelectorAll('[data-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.modal?.getAttribute('aria-hidden') === 'false';
    }

    async open(handle) {
      try {
        const response = await fetch(`/products/${handle}?view=quick-view`);
        const html = await response.text();
        
        if (this.modalContent) {
          this.modalContent.innerHTML = html;
        }
        
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        
        // Reinitialize variants for the modal
        new ProductVariants();
        new QuantitySelector();
      } catch (error) {
        console.error('Error loading quick view:', error);
      }
    }

    close() {
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  // ========================================
  // MOBILE MENU
  // ========================================
  
  class MobileMenu {
    constructor() {
      this.toggle = document.querySelector('[data-mobile-menu-toggle]');
      this.menu = document.querySelector('[data-mobile-menu]');
      
      if (!this.toggle) return;
      
      this.bindEvents();
    }

    bindEvents() {
      this.toggle.addEventListener('click', () => this.toggleMenu());
    }

    toggleMenu() {
      // Mobile menu implementation
      document.body.classList.toggle('mobile-menu-open');
    }
  }

  // ========================================
  // SEARCH
  // ========================================
  
  class Search {
    constructor() {
      this.toggle = document.querySelector('[data-search-toggle]');
      this.drawer = document.querySelector('[data-search-drawer]');
      this.closeButtons = document.querySelectorAll('[data-search-close]');
      this.input = this.drawer?.querySelector('.search-drawer__input');
      
      if (!this.toggle) return;
      
      this.bindEvents();
    }

    bindEvents() {
      this.toggle.addEventListener('click', () => this.open());
      
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });
      
      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });
    }

    isOpen() {
      return this.drawer?.getAttribute('aria-hidden') === 'false';
    }

    open() {
      if (!this.drawer) return;
      
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus the input after animation and trap focus
      setTimeout(() => {
        this.input?.focus();
        this.removeFocusTrap = trapFocus(this.drawer.querySelector('.search-drawer__content') || this.drawer);
      }, 300);
    }

    close() {
      if (!this.drawer) return;
      
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Remove focus trap
      if (this.removeFocusTrap) {
        this.removeFocusTrap();
        this.removeFocusTrap = null;
      }
    }
  }

  // ========================================
  // INITIALIZATION
  // ========================================
  
  // Enforce cart quantity limits on page load
  async function enforceCartLimits() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const itemsToUpdate = cart.items.filter(item => item.quantity > MAX_QTY_PER_PRODUCT);
      
      if (itemsToUpdate.length > 0) {
        for (const item of itemsToUpdate) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: MAX_QTY_PER_PRODUCT })
          });
        }
        
        // Update cart count display
        const updatedResponse = await fetch('/cart.js');
        const updatedCart = await updatedResponse.json();
        document.querySelectorAll('.header__cart-count').forEach((el) => {
          if (updatedCart.item_count > 0) {
            el.textContent = updatedCart.item_count;
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });
      }
    } catch (error) {
      console.error('Error enforcing cart limits:', error);
    }
  }

  // ========================================
  // PRODUCT CARD CLICK HANDLING
  // ========================================
  
  // Make product card info section clickable (same approach as collection cards)
  function initProductCardClicks() {
  // No JS needed; rely on native link wrapping the entire card.
  }

  // ========================================
  // PRODUCT CARD IMAGE PRELOADER
  // ========================================
  // Preload secondary (hover) images and product-page main image when cards enter viewport,
  // and preload product-page image on hover so it's cached when user opens the product.
  const preloadedImageUrls = new Set();
  function preloadImage(url) {
    if (!url || preloadedImageUrls.has(url)) return;
    preloadedImageUrls.add(url);
    const img = new Image();
    img.src = url;
  }
  function preloadProductCardImages(card) {
    const secondaryUrl = card.getAttribute('data-secondary-image-url');
    const productPageUrl = card.getAttribute('data-product-page-image');
    if (secondaryUrl) preloadImage(secondaryUrl);
    if (productPageUrl) preloadImage(productPageUrl);
  }
  function initProductCardImagePreloader() {
    const cards = document.querySelectorAll('.product-card[data-product-card]');
    if (!cards.length) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) preloadProductCardImages(entry.target);
      });
    }, { rootMargin: '100px', threshold: 0.01 });
    cards.forEach((card) => observer.observe(card));
    // On hover, preload product page image immediately so it's ready when they click
    cards.forEach((card) => {
      const link = card.querySelector('.product-card__link');
      if (!link) return;
      link.addEventListener('mouseenter', () => {
        const url = card.getAttribute('data-product-page-image');
        if (url) preloadImage(url);
      }, { once: false });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Enforce cart limits immediately
    enforceCartLimits();
    
    // Initialize all components
    const cart = new Cart();
    new ScrollAnimations();
    new ProductGallery();
    new ProductVariants();
    new QuantitySelector();
    new QuickView();
    new MobileMenu();
    new Search();
    
    // Initialize product card click handling
    initProductCardClicks();
    // Preload secondary + product-page images when cards enter viewport; product-page image also on hover
    initProductCardImagePreloader();
    
    // Open cart drawer if redirected from cart page
    if (sessionStorage.getItem('openCartDrawer') === 'true') {
      sessionStorage.removeItem('openCartDrawer');
      // Small delay to ensure drawer is initialized
      setTimeout(() => {
        cart.open();
      }, 100);
    }

    // Add smooth scrolling
    document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
      anchor.addEventListener('click', (e) => {
        const href = anchor.getAttribute('href');
        if (href !== '#') {
          e.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    });

    console.log('🎨 Umuloo Theme Initialized');
  });

})();

// Notification Styles (injected)
const notificationStyles = document.createElement('style');
notificationStyles.textContent = `
  #notification-container {
    position: fixed;
    top: calc(var(--header-height, 70px) + var(--announcement-height, 36px) + 16px);
    right: 16px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 10px;
    pointer-events: none;
  }
  
  .notification {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    transform: translateX(120%);
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: auto;
    max-width: 360px;
  }
  
  .notification--visible {
    transform: translateX(0);
    opacity: 1;
  }
  
  .notification--hiding {
    transform: translateX(120%);
    opacity: 0;
  }
  
  .notification--success {
    border-left: 4px solid #84A27B;
  }
  
  .notification--success .notification__icon {
    color: #84A27B;
  }
  
  .notification--error {
    border-left: 4px solid #e74c3c;
  }
  
  .notification--error .notification__icon {
    color: #e74c3c;
  }
  
  .notification--warning {
    border-left: 4px solid #f39c12;
  }
  
  .notification--warning .notification__icon {
    color: #f39c12;
  }
  
  .notification__icon {
    flex-shrink: 0;
  }
  
  .notification__message {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #3D3D3D;
    line-height: 1.4;
  }
  
  .notification__close {
    flex-shrink: 0;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #7A7A7A;
    transition: color 0.2s;
    border-radius: 4px;
  }
  
  .notification__close:hover {
    color: #3D3D3D;
    background: rgba(0, 0, 0, 0.05);
  }
  
  /* Notifications disabled globally */
  #notification-container {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
  
  .notification {
    display: none !important;
  }
`;
document.head.appendChild(notificationStyles);

// Aggressively remove all notifications on page load and continuously
(function() {
  function removeAllNotifications() {
    // Remove notification container
    const container = document.getElementById('notification-container');
    if (container) {
      container.remove();
    }
    
    // Remove cart notification
    const cartNotification = document.getElementById('cart-notification');
    if (cartNotification) {
      cartNotification.remove();
    }
    
    // Remove any notification elements
    document.querySelectorAll('.notification, .cart-notification, #notification-container').forEach(el => {
      el.remove();
    });
  }
  
  // Run immediately
  removeAllNotifications();
  
  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', removeAllNotifications);
  } else {
    removeAllNotifications();
  }
  
  // Run periodically to catch dynamically added notifications
  setInterval(removeAllNotifications, 100);
  
  // Also remove on any DOM mutations
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(removeAllNotifications);
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();

// Remove select elements on mobile devices
(function() {
  // Inject a style tag with aggressive CSS rules for mobile
  function injectMobileSelectHideCSS() {
    if (window.innerWidth <= 768) {
      const styleId = 'mobile-select-hide';
      let styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
          select,
          select:not(.hidden-on-mobile),
          .product-form select,
          .product__form select,
          form select,
          .product__variants select,
          [name*="option"] select,
          .variant-selector select,
          .product-single__variants select,
          .product-form__item select,
          .product-card__quick-add-wrap select,
          .product-card__quick-add-form select,
          .product-card select,
          .product-card__link select {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
            position: absolute !important;
            left: -9999px !important;
            width: 0 !important;
            height: 0 !important;
            overflow: hidden !important;
            margin: 0 !important;
            padding: 0 !important;
            border: none !important;
            clip: rect(0, 0, 0, 0) !important;
          }
        `;
        document.head.appendChild(styleEl);
      }
    } else {
      // Remove the style tag on desktop
      const styleEl = document.getElementById('mobile-select-hide');
      if (styleEl) {
        styleEl.remove();
      }
    }
  }

  function removeSelectElements() {
    // Check if mobile (768px or less, or touch device)
    const isMobile = window.innerWidth <= 768 || ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    
    if (isMobile) {
      // Inject CSS first
      injectMobileSelectHideCSS();
      
      // Get all select elements with various selectors
      const selects = document.querySelectorAll('select, form select, .product-form select, .product__form select, .product__variants select, [name*="option"] select, .variant-selector select, .product-single__variants select, .product-form__item select, .product-card__quick-add-wrap select, .product-card__quick-add-form select, .product-card select, .product-card__link select');
      
      selects.forEach(select => {
        // Apply multiple hiding techniques
        select.style.setProperty('display', 'none', 'important');
        select.style.setProperty('visibility', 'hidden', 'important');
        select.style.setProperty('opacity', '0', 'important');
        select.style.setProperty('pointer-events', 'none', 'important');
        select.style.setProperty('position', 'absolute', 'important');
        select.style.setProperty('left', '-9999px', 'important');
        select.style.setProperty('width', '0', 'important');
        select.style.setProperty('height', '0', 'important');
        select.style.setProperty('overflow', 'hidden', 'important');
        select.style.setProperty('margin', '0', 'important');
        select.style.setProperty('padding', '0', 'important');
        select.style.setProperty('border', 'none', 'important');
        select.setAttribute('aria-hidden', 'true');
        select.setAttribute('tabindex', '-1');
        
        // Also try to remove from DOM if it's not needed
        const parent = select.parentElement;
        if (parent && parent.classList.contains('product__variants')) {
          // Don't remove if it's in product variants, just hide it
        }
      });
    } else {
      // Remove style tag on desktop
      const styleEl = document.getElementById('mobile-select-hide');
      if (styleEl) {
        styleEl.remove();
      }
    }
  }

  // Inject CSS immediately
  injectMobileSelectHideCSS();
  
  // Remove immediately on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      removeSelectElements();
      injectMobileSelectHideCSS();
    });
  } else {
    removeSelectElements();
    injectMobileSelectHideCSS();
  }

  // Remove immediately and on window load
  window.addEventListener('load', function() {
    removeSelectElements();
    injectMobileSelectHideCSS();
  });
  
  // Remove on window resize in case device orientation changes
  window.addEventListener('resize', function() {
    removeSelectElements();
    injectMobileSelectHideCSS();
  });
  
  // Remove on orientation change
  window.addEventListener('orientationchange', function() {
    setTimeout(function() {
      removeSelectElements();
      injectMobileSelectHideCSS();
    }, 100);
  });

  // Also remove on any DOM mutations (for dynamically added selects)
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      let shouldRemove = false;
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              if (node.tagName === 'SELECT' || node.querySelectorAll && node.querySelectorAll('select').length > 0) {
                shouldRemove = true;
              }
            }
          });
        }
      });
      if (shouldRemove) {
        setTimeout(function() {
          removeSelectElements();
          injectMobileSelectHideCSS();
        }, 0);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: false });
  }
  
  // Run periodically to catch any that slip through
  setInterval(function() {
    removeSelectElements();
    injectMobileSelectHideCSS();
  }, 500);
})();